import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../context/TranslationContext';

const RoomImageSlider = ({ 
  images, 
  roomName, 
  discount, 
  rating, 
  onImageClick, 
  showControls = true, 
  autoSlide = false,
  className = "",
  size = "medium", // small, medium, large
  externalCurrentIndex = null,
  onImageChange = null,
  roomsAvailable = null // Number of rooms available (for "Last Room" badge)
}) => {
  const { t } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Debug logging
  useEffect(() => {
    // console.log(`RoomImageSlider mounted: ${images?.length || 0} images, autoSlide: ${autoSlide}, currentIndex: ${currentImageIndex}`);
  }, []);

  // Sync with external index if provided
  useEffect(() => {
    if (externalCurrentIndex !== null) {
      // console.log(`Syncing external index: ${externalCurrentIndex}`);
      setCurrentImageIndex(externalCurrentIndex);
    }
  }, [externalCurrentIndex]);

  // Auto-slide functionality
  useEffect(() => {
    if (!autoSlide || !images || images.length <= 1) return;

    // console.log(`Setting up auto-slide for ${images.length} images, current index: ${currentImageIndex}`);

    const interval = setInterval(() => {
      if (!isPaused) {
        setCurrentImageIndex(prevIndex => {
          const newIndex = (prevIndex + 1) % images.length;
          // console.log(`Auto-sliding from ${prevIndex} to ${newIndex} (${images.length} total images)`);
          if (onImageChange) {
            onImageChange(newIndex);
          }
          return newIndex;
        });
      }
    }, 3000); // Reduced to 3 seconds for testing

    return () => clearInterval(interval);
  }, [images, autoSlide, isPaused, onImageChange]);

  // Handle image navigation
  const goToImage = (index) => {
    setCurrentImageIndex(index);
    if (onImageChange) {
      onImageChange(index);
    }
  };

  const nextImage = () => {
    const newIndex = (currentImageIndex + 1) % images.length;
    setCurrentImageIndex(newIndex);
    if (onImageChange) {
      onImageChange(newIndex);
    }
  };

  const prevImage = () => {
    const newIndex = (currentImageIndex - 1 + images.length) % images.length;
    setCurrentImageIndex(newIndex);
    if (onImageChange) {
      onImageChange(newIndex);
    }
  };

  // Size classes
  const sizeClasses = {
    small: "w-full h-32 md:h-40",
    medium: "w-full md:w-72 h-48 md:h-64",
    large: "w-full h-64 md:h-80 lg:h-96 ",
  };

  // If no images, show fallback
  if (!images || images.length === 0) {
    return (
      <div className={`relative ${sizeClasses[size]} bg-gray-200 rounded-lg flex items-center justify-center`}>
        <div className="text-gray-500 text-center">
          <svg className="h-12 w-12 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
          </svg>
          <p className="text-sm">No images available</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`relative ${sizeClasses[size]} flex-shrink-0 group overflow-hidden rounded-lg cursor-pointer border border-gray-100 ${className}`}
      onMouseEnter={() => {
        // console.log('Mouse entered - pausing auto-slide');
        setIsPaused(true);
      }}
      onMouseLeave={() => {
        // console.log('Mouse left - resuming auto-slide');
        setIsPaused(false);
      }}
    >
      {/* Main Image */}
      <img
        src={images[currentImageIndex]}
        alt={`${roomName} - Image ${currentImageIndex + 1}`}
        className="w-full h-full object-cover transition-all duration-500 ease-in-out group-hover:scale-[1.02] group-hover:brightness-[1.05]"
        onClick={onImageClick}
        loading="lazy"
      />

      {/* Image Loading Overlay - More subtle */}
      <div className="absolute inset-0 bg-gray-100 opacity-0 group-hover:opacity-10 transition-opacity duration-100" />

      {/* Last Room Badge - Top Left */}
      {roomsAvailable !== null && roomsAvailable > 0 && roomsAvailable <= 5 && (
        <div className="absolute top-3 left-3 bg-teal-500 text-white px-3 py-1.5 rounded-md text-sm font-semibold shadow-lg ">
          {roomsAvailable === 1 ? t('search.lastRoom') : t('search.lastRooms', { n: roomsAvailable })}
        </div>
      )}

      {/* Image Counter Badge - Bottom Left */}
      {images.length > 1 && (
        <div className="absolute bottom-3 left-3 bg-black bg-opacity-70 text-white px-2 py-1 rounded-full text-xs font-medium backdrop-blur-sm">
          {currentImageIndex + 1} / {images.length}
        </div>
      )}

      {/* Discount Badge - More professional */}
      {discount && (
        <div className="absolute top-3 left-3 bg-red-500 text-white px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm">
          {discount}
        </div>
      )}

      {/* Rating Badge - More professional */}
      {rating && (
        <div className="absolute top-16 left-3 bg-black bg-opacity-70 text-white px-2 py-1 rounded-full flex items-center space-x-1.5 shadow-sm">
          <svg className="h-3.5 w-3.5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          <span className="text-xs font-medium">{rating}</span>
        </div>
      )}

      {/* Navigation Controls - Only show if showControls is true */}
      {showControls && images.length > 1 && (
        <>
          {/* Previous Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-95 text-gray-700 p-1.5 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-opacity-100 hover:scale-105"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </button>

          {/* Next Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-95 text-gray-700 p-1.5 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-opacity-100 hover:scale-105"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </button>
        </>
      )}

      {/* Image Dots Indicator */}
      {images.length > 1 && (
        <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                goToImage(index);
              }}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-200 cursor-pointer hover:scale-110 ${
                index === currentImageIndex 
                  ? 'bg-white shadow-sm scale-100' 
                  : 'bg-white bg-opacity-60 hover:bg-opacity-80'
              }`}
            />
          ))}
        </div>
      )}

      {/* Auto-slide Progress Bar - More subtle */}
      {images.length > 1 && autoSlide && (
        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-black bg-opacity-10">
          {/* <div 
            className="h-full bg-white transition-all duration-500 ease-out"
            style={{ 
              width: `${(currentImageIndex / (images.length - 1)) * 100}%` 
            }}
          /> */}
          {/* Auto-slide indicator */}
          <div className="absolute top-1 right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" title="Auto-slide active" />
        </div>
      )}

      {/* Fullscreen/Expand Button */}
      {onImageClick && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onImageClick();
          }}
          className="absolute bottom-3 right-3 bg-black bg-opacity-60 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-opacity-80 hover:scale-105"
        >
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12z" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default RoomImageSlider;
